import { ref } from 'vue'
import { useUIStore } from '@/stores/ui'
import apiInterceptor from '@/shared/apiInterceptor'
import { environment } from '@/shared/env'
import { normaliseBsuid } from '@/helpers/bsuid.helper'

/**
 * Pure utility — kept local to avoid a dependency on the Options API chat mixin.
 * Returns the number of hours since the customer's last reply.
 */
function hoursSinceLastReply(input) {
  if (!input || input === 'None') return 10000
  try {
    const isoUTC = input.replace(' ', 'T').replace(/(\.\d{3})\d*/, '$1') + 'Z'
    const utcDate = new Date(isoUTC)
    if (isNaN(utcDate)) throw new Error('Invalid date')
    return (Date.now() - utcDate.getTime()) / (1000 * 60 * 60)
  } catch {
    return 10000
  }
}

/**
 * Manages the customer list and pagination.
 *
 * Pagination rule: fetchList() is the ONLY method here.
 * Callers in index.vue decide whether to also refresh stats — this composable never does.
 */
export function useCustomerList() {
  const _uiStore = useUIStore()

  const customers = ref([])
  const loading = ref(false)
  const currentPage = ref(1)
  const currentPageSize = ref(5)
  const isSearchMode = ref(false)
  const isChannelFiltered = ref(false)

  // ── Private ──────────────────────────────────────────────────────────────────

  function _normaliseUser(raw) {
    if (typeof raw.conversationid === 'undefined') raw.conversationid = raw.id
    if (!raw.name || raw.name === 'None') raw.name = 'unrecorded'
    if (!raw.email || raw.email === 'None') raw.email = 'unrecorded'
    if (raw.active == null) raw.active = true
    raw.bsuid = normaliseBsuid(raw.bsuid)

    return {
      ...raw,
      id: raw.conversationid,
      currentlyOpendChat: false,
      channel_id: raw.channel,
      channel: raw.platform,
      page_msg_subscription_restriction:
        raw.platform === 'whatsapp' && hoursSinceLastReply(raw.userReplyTimestamp) >= 24,
    }
  }

  // ── Public ───────────────────────────────────────────────────────────────────

  /**
   * Fetches one page of customers.
   * @param {object} searchParams - Query params to send to the API.
   * @param {number} page         - 1-based page index.
   * @param {number} pageSize     - Number of items per page.
   */
  async function fetchList(searchParams = {}, page = 1, pageSize = 5) {
    try {
      loading.value = true
      currentPage.value = page
      currentPageSize.value = pageSize

      const isSearch = Object.keys(searchParams).length > 0
      isChannelFiltered.value = Object.prototype.hasOwnProperty.call(searchParams, 'channel')
      const hasSegment = Object.prototype.hasOwnProperty.call(searchParams, 'segmentid')

      // Non-channel, non-segment searches require full user objects for correct display
      const isDeepSearch = isSearch && !isChannelFiltered.value && !hasSegment

      isSearchMode.value = isSearch && !isChannelFiltered.value

      const params = {
        size: currentPageSize.value,
        ...(page > 1 && { offset: (page - 1) * currentPageSize.value }),
        channel: 'all',
        retarget: 'both',
        minimal: isDeepSearch ? 'false' : 'true',
        ...(isDeepSearch && { fetch_disconnected: 'true' }),
        ...searchParams,
      }

      const response = await apiInterceptor.get(
        environment('VUE_APP_API_URL') + 'crm/v2/customers/',
        { params },
      )

      // Segment/channel searches return paginated data; text searches return user_info array
      const rawUsers = !isSearch || isChannelFiltered.value || hasSegment
        ? response.data
        : response.user_info

      if (hasSegment) isSearchMode.value = true

      customers.value = (rawUsers ?? []).map(_normaliseUser)
    } catch (error) {
      _uiStore.updateToast({ open: true, message: error.message, type: 'danger' })
    } finally {
      loading.value = false
    }
  }

  function clearList() {
    customers.value = []
    currentPage.value = 1
    currentPageSize.value = 5
    isSearchMode.value = false
    isChannelFiltered.value = false
  }

  return {
    customers,
    loading,
    currentPage,
    currentPageSize,
    isSearchMode,
    isChannelFiltered,
    fetchList,
    clearList,
  }
}
